import { Cheesery } from '../types';
import { imageUrl } from './images';

/**
 * Curated Vermont and Maine artisan cheesemakers.
 *
 * Sources: Vermont Cheese Council member directory (vtcheese.com/members),
 * Maine Cheese Guild creamery map (mainecheeseguild.org), and each producer's
 * public-facing website. Coordinates are approximate (town-level when no
 * public visitor address is listed) and hours are subject to seasonal change.
 *
 * Fact-check before shipping: every `hours` string and `visitorPolicy` value
 * has a tendency to drift year-to-year. Treat this file as a living document.
 */
export const CHEESERIES: Cheesery[] = [
  // ----------------------------------------------------------------
  // VERMONT
  // ----------------------------------------------------------------
  {
    id: 'shelburne-farms',
    type: 'cheesery',
    verified: true,
    region: 'champlain-valley',
    name: 'Shelburne Farms',
    description:
      'A 1,400-acre working farm and education non-profit on Lake Champlain. The farmhouse cheddar is aged one to two years and made from the raw milk of the resident Brown Swiss herd. Visitors can walk the Farm Trail, sample cheese at the Welcome Center & Farm Store, and watch cheesemaking from a viewing window during production days.',
    address: '1611 Harbor Rd',
    city: 'Shelburne',
    state: 'VT',
    lat: 44.3942,
    lng: -73.2307,
    image: imageUrl('red-barn-silo'),
    imageKey: 'red-barn-silo',
    hours: 'Welcome Center open daily, typically 10am–5pm (seasonal variation)',
    website: 'https://shelburnefarms.org/',
    phone: '(802) 985-8686',
    highlights: ['Lake Champlain views', 'Farm Store', 'Walking trails', 'Cheesemaking viewing window'],
    milkTypes: ['Cow'],
    styles: ['Cheddar'],
    tastingAvailable: true,
    visitorPolicy: 'open-to-public',
    history:
      'Founded in 1886 by Dr. William Seward and Lila Vanderbilt Webb as a model agricultural estate, Shelburne Farms transitioned to a non-profit in 1972. It now operates as an education center and working farm, using cheese sales to fund conservation and school programs.',
    pairings: ['Apple slices', 'Whole-grain mustard', 'Local ale'],
    costRange: '$$',
  },
  {
    id: 'cabot-creamery-waterbury',
    type: 'cheesery',
    verified: true,
    region: 'mad-river-green-mountains',
    name: 'Cabot Creamery Visitor Center',
    description:
      'Flagship retail experience for the farmer-owned Cabot cooperative, which represents roughly 500 dairy farm families across New England and New York. The Waterbury Center store offers free cheddar tastings across the full Cabot range — from Seriously Sharp to specialty extra-aged, flavored, and limited-edition cheeses — plus a curated selection of Vermont pantry items.',
    address: '2657 Waterbury-Stowe Rd',
    city: 'Waterbury Center',
    state: 'VT',
    lat: 44.3764,
    lng: -72.7379,
    image: imageUrl('cheese-board-charcuterie'),
    imageKey: 'cheese-board-charcuterie',
    hours: 'Tue–Sun 10:30am–5pm (closed Mondays, hours vary by season)',
    website: 'https://cabotcreamery.com/pages/visit-us',
    phone: '(802) 244-6334',
    highlights: ['Free tastings', 'Cooperative of 500+ farms', 'Cabot Cruiser food truck (seasonal)'],
    milkTypes: ['Cow'],
    styles: ['Cheddar', 'Aged Hard'],
    tastingAvailable: true,
    visitorPolicy: 'open-to-public',
    history:
      'Cabot was founded in 1919 when 94 farmers along Cabot Common banded together to process their surplus milk into butter. It has since become one of the most recognized cheddar brands in the U.S. and a Certified B Corporation.',
    pairings: ['Cortland apples', 'Cider donuts', 'Maple mustard'],
    costRange: '$',
  },
  {
    id: 'grafton-village-cheese-brattleboro',
    type: 'cheesery',
    verified: true,
    region: 'southern-vermont',
    name: 'Grafton Village Cheese – Brattleboro',
    description:
      'The larger of Grafton Village’s two retail shops, with a self-guided viewing gallery overlooking the cheesemaking floor. Specializes in raw-milk Vermont cheddar aged from one to four years, plus smoke-kissed and maple-rubbed variants. Most cheeses in the case can be sampled before you buy.',
    address: '400 Linden St',
    city: 'Brattleboro',
    state: 'VT',
    lat: 42.8624,
    lng: -72.5806,
    image: imageUrl('cheese-sliced-wooden'),
    imageKey: 'cheese-sliced-wooden',
    hours: 'Daily 10am–5pm (hours vary seasonally)',
    website: 'https://graftonvillagecheese.com/',
    phone: '(802) 246-2221',
    highlights: ['Cheesemaking viewing gallery', 'Wide cheddar age range', 'Gift-shipping'],
    milkTypes: ['Cow'],
    styles: ['Cheddar', 'Aged Hard'],
    tastingAvailable: true,
    visitorPolicy: 'open-to-public',
    history:
      'The original Grafton Co-operative Cheese Company was formed in 1892 by dairy farmers in Grafton, VT. Rebuilt in 1965 by The Windham Foundation to revive the village’s economy, the company still ages its flagship clothbound cheddar using traditional methods.',
    pairings: ['Pepper jelly', 'Cured meats', 'Dark chocolate'],
    costRange: '$$',
  },
  {
    id: 'plymouth-artisan-cheese',
    type: 'cheesery',
    verified: true,
    region: 'southern-vermont',
    name: 'Plymouth Artisan Cheese',
    description:
      'Housed inside the Calvin Coolidge State Historic Site, Plymouth is the second-oldest operating cheese factory in the United States. Self-guided tours pass viewing windows into the make room and an upstairs museum of antique cheesemaking equipment. Granular-curd cheeses — including the distinctive original Plymouth — are for sale in the small retail shop.',
    address: '106 Messer Hill Rd',
    city: 'Plymouth Notch',
    state: 'VT',
    lat: 43.5331,
    lng: -72.7200,
    image: imageUrl('red-barn-large'),
    imageKey: 'red-barn-large',
    hours: 'Open seasonally late-May through mid-October; off-season by chance',
    website: 'https://plymouthcheese.com/',
    phone: '(802) 672-3650',
    highlights: ['Founded 1890', 'Historic site context', 'Granular-curd cheeses'],
    milkTypes: ['Cow'],
    styles: ['Aged Hard'],
    tastingAvailable: true,
    visitorPolicy: 'seasonal',
    history:
      'Founded in 1890 by John Coolidge — father of the 30th U.S. President, Calvin Coolidge — the factory closed in 1934 and was revived in 2009 after decades of dormancy. It continues to use granular-curd recipes believed to pre-date cheddar.',
    pairings: ['Hard cider', 'Stone fruit jam', 'Buttery crackers'],
    costRange: '$$',
  },
  {
    id: 'crowley-cheese',
    type: 'cheesery',
    verified: true,
    region: 'southern-vermont',
    name: 'Crowley Cheese',
    description:
      'Widely considered the oldest continuously operating cheese factory in America, the 1882 wooden building in rural Mount Holly still makes raw-milk colby-style cheese entirely by hand. On production days (typically Tuesday–Thursday) visitors can watch the cheesemaker stir, cut, and rake curds; the small shop sells cheese, mustards, and pantry goods.',
    address: '14 Crowley Ln',
    city: 'Mount Holly',
    state: 'VT',
    lat: 43.4390,
    lng: -72.7775,
    image: imageUrl('cheese-board-charcuterie'),
    imageKey: 'cheese-board-charcuterie',
    hours: 'Shop daily 10am–4pm; cheese being made ~3 weekdays (call ahead)',
    website: 'https://crowleycheese.com/',
    phone: '(802) 259-2340',
    highlights: ['Listed on the National Register of Historic Places', 'Handmade colby-style', 'Production viewing'],
    milkTypes: ['Cow'],
    styles: ['Aged Hard'],
    tastingAvailable: true,
    visitorPolicy: 'open-to-public',
    history:
      'The Crowley family began making cheese in their Healdville farmhouse in 1824 and built the current factory in 1882. The building and its methods are largely unchanged; the factory is listed on the National Register of Historic Places.',
    pairings: ['Apple butter', 'Sourdough', 'Cold IPA'],
    costRange: '$$',
  },
  {
    id: 'sugarbush-farm',
    type: 'cheesery',
    verified: true,
    region: 'mad-river-green-mountains',
    name: 'Sugarbush Farm',
    description:
      'A 500-acre hilltop farm outside Woodstock offering free self-guided tours of its maple sugarhouse and cheese room, plus generous tastings of around fifteen wax-sealed cheeses — including smoked and herb variants — alongside four grades of Vermont maple syrup. A long-time family-run favorite for a low-key, kid-friendly farm visit.',
    address: '591 Sugarbush Farm Rd',
    city: 'Woodstock',
    state: 'VT',
    lat: 43.6432,
    lng: -72.4667,
    image: imageUrl('dairy-cow-black-white'),
    imageKey: 'dairy-cow-black-white',
    hours: 'Typically daily 9am–5pm; road access can be rough in mud season',
    website: 'https://sugarbushfarm.com/',
    phone: '(802) 457-1757',
    highlights: ['Free tastings', 'Maple sugarhouse tour', 'Family-run since 1945'],
    milkTypes: ['Cow'],
    styles: ['Cheddar', 'Aged Hard'],
    tastingAvailable: true,
    visitorPolicy: 'open-to-public',
    history:
      'Jack and Betty Ayers bought the hill farm in 1945 and began waxing their own cheese and boiling sap. Three generations later the Luce family continues operations, shipping wax-sealed cheese nationwide.',
    pairings: ['Vermont maple syrup', 'Apple slices', 'Water crackers'],
    costRange: '$',
  },
  {
    id: 'von-trapp-farmstead',
    type: 'cheesery',
    verified: true,
    region: 'mad-river-green-mountains',
    name: 'von Trapp Farmstead',
    description:
      'A certified-organic farmstead cheesemaker in the Mad River Valley run by the American branch of the von Trapp family. Milk from their own Jersey and Holstein herd becomes Oma (a washed-rind, raw-milk Alpine-style with Jasper Hill), Mad River Blue, Savage, and other seasonal rounds. The farm store carries all in-house cheeses and yogurts plus goods from 40+ Vermont producers.',
    address: '1821 German Flats Rd',
    city: 'Waitsfield',
    state: 'VT',
    lat: 44.1675,
    lng: -72.8450,
    image: imageUrl('cow-mountain-pasture'),
    imageKey: 'cow-mountain-pasture',
    hours: 'Farm Store typically Thu–Sun; check website for current schedule',
    website: 'https://vontrappfarmstead.com/',
    highlights: ['Certified organic', 'Farm store', 'Jersey-Holstein herd', 'Oma washed-rind'],
    milkTypes: ['Cow'],
    styles: ['Alpine', 'Blue', 'Soft Ripened'],
    tastingAvailable: true,
    visitorPolicy: 'open-to-public',
    history:
      'Werner and Erika von Trapp, son and daughter-in-law of the family immortalized in The Sound of Music, settled in Waitsfield in 1959. The farmstead began making cheese commercially in 2009 under the third generation.',
    pairings: ['Stone-ground mustard', 'Pickled ramps', 'Austrian riesling'],
    costRange: '$$',
  },
  {
    id: 'jasper-hill-farm',
    type: 'cheesery',
    verified: true,
    region: 'northeast-kingdom',
    name: 'Jasper Hill Farm',
    description:
      'Arguably the most decorated cheesemaker in the United States. Jasper Hill operates both a 22,000-square-foot natural-rind aging cave system and a working farm. The farm itself is closed to the public for bio-security reasons, but the full range — Bayley Hazen Blue, Harbison, Winnimere, Cabot Clothbound aged in their cellars, and more — is consistently stocked at nearby Willey’s Store in Greensboro Village and at Hill Farmstead’s beer garden.',
    address: 'Garvin Hill Rd (no farm visits; buy in Greensboro Village)',
    city: 'Greensboro',
    state: 'VT',
    lat: 44.5772,
    lng: -72.2934,
    image: imageUrl('cheese-sliced-wooden'),
    imageKey: 'cheese-sliced-wooden',
    hours: 'Farm closed to public; cheese sold at Willey’s Store and regional retailers',
    website: 'https://jasperhillfarm.com/',
    highlights: ['National Cheese Champion titles', 'Natural-rind aging cellars', 'Buy at Willey’s Store'],
    milkTypes: ['Cow'],
    styles: ['Soft Ripened', 'Blue', 'Alpine', 'Aged Hard'],
    tastingAvailable: false,
    visitorPolicy: 'retail-partner',
    history:
      'Brothers Andy and Mateo Kehler bought the farm in 1998 and began making cheese in 2003. The Cellars at Jasper Hill opened in 2008 to age cheeses from partner producers, catapulting Vermont onto the global cheese map.',
    pairings: ['Quince paste', 'Toasted walnuts', 'Vermont cider'],
    costRange: '$$$',
  },
  {
    id: 'vermont-creamery',
    type: 'cheesery',
    verified: true,
    region: 'southern-vermont',
    name: 'Vermont Creamery',
    description:
      'One of the first American producers to bring French-style goat and cultured-butter making to the U.S. retail shelf. Bonne Bouche, Coupole, and Cremont are among the awarded soft-ripened rounds; the cultured butter and crème fraîche are pastry-kitchen staples nationwide. The Websterville facility is production-only; look for the full line-up at Vermont specialty grocers and co-ops.',
    address: '40 Pitman Rd (production only — no visitor center)',
    city: 'Websterville',
    state: 'VT',
    lat: 44.1717,
    lng: -72.4631,
    image: imageUrl('goat-portrait'),
    imageKey: 'goat-portrait',
    hours: 'No on-site visits; cheese widely available at VT retailers',
    website: 'https://vermontcreamery.com/',
    highlights: ['Bonne Bouche', 'Cultured butter', 'Soft-ripened goat rounds'],
    milkTypes: ['Goat', 'Cow'],
    styles: ['Fresh', 'Soft Ripened'],
    tastingAvailable: false,
    visitorPolicy: 'production-only',
    history:
      'Co-founded in 1984 by Allison Hooper and Bob Reese, Vermont Creamery helped define American farmstead-style goat cheese. Now a Certified B Corp, it became a subsidiary of Land O’Lakes in 2017.',
    pairings: ['Lavender honey', 'Fresh berries', 'Baguette'],
    costRange: '$$',
  },
  {
    id: 'spring-brook-farm',
    type: 'cheesery',
    verified: true,
    region: 'mad-river-green-mountains',
    name: 'Spring Brook Farm',
    description:
      'A working Jersey-cow dairy and non-profit (Farms for City Kids) whose Tarentaise, Reading, and Ashbrook cheeses are Alpine-inspired wheels aged in their own vault. Tours are by appointment; the farm primarily hosts visiting schoolchildren but welcomes cheese-interested visitors who reach out ahead.',
    address: '3836 Vermont Route 106',
    city: 'Reading',
    state: 'VT',
    lat: 43.5077,
    lng: -72.5480,
    image: imageUrl('cow-mountain-pasture'),
    imageKey: 'cow-mountain-pasture',
    hours: 'Tours by appointment only',
    website: 'https://sbfcheese.org/',
    highlights: ['Alpine-style wheels', 'Non-profit education farm', 'Jersey herd'],
    milkTypes: ['Cow'],
    styles: ['Alpine', 'Aged Hard'],
    tastingAvailable: true,
    visitorPolicy: 'by-appointment',
    history:
      'Founded in 1990 by investment-banker-turned-educator Tim Goltz as Farms for City Kids, Spring Brook began making cheese in 2008 to generate revenue that supports week-long farm-stay programs for urban middle-schoolers.',
    pairings: ['Stone fruit', 'Smoked almonds', 'White Burgundy'],
    costRange: '$$$',
  },
  {
    id: 'blue-ledge-farm',
    type: 'cheesery',
    verified: true,
    region: 'champlain-valley',
    name: 'Blue Ledge Farm',
    description:
      'A first-generation family goat dairy in the Champlain Valley producing aged chèvre rounds and bloomy-rind discs such as Middlebury Blue and Riley’s Coat. Tours are by appointment; the most common public encounters are at Middlebury Farmers Market and co-op shelves around the state.',
    address: '2001 Arnold District Rd',
    city: 'Salisbury',
    state: 'VT',
    lat: 43.9168,
    lng: -73.1257,
    image: imageUrl('goat-on-grass'),
    imageKey: 'goat-on-grass',
    hours: 'Farm visits by appointment; find cheese at VT farmers markets',
    website: 'https://blueledgefarm.com/',
    highlights: ['Chèvre', 'Middlebury Blue', 'Goat + cow blends'],
    milkTypes: ['Goat', 'Cow'],
    styles: ['Blue', 'Soft Ripened', 'Fresh'],
    tastingAvailable: true,
    visitorPolicy: 'by-appointment',
    history:
      'Greg Bernhardt and Hannah Sessions left graduate school in 2000 to start the farm on rented pasture outside Middlebury. They now run a 150-goat herd and are known for their vocal stewardship of small-scale farming.',
    pairings: ['Fig jam', 'Candied pecans', 'Port'],
    costRange: '$',
  },
  {
    id: 'vermont-shepherd',
    type: 'cheesery',
    verified: true,
    region: 'southern-vermont',
    name: 'Vermont Shepherd',
    description:
      'One of the earliest American producers of sheep-milk cheese in the Pyrenees tradition. Verano (summer-milk wheels) and Invierno (cow-sheep blend) are hand-washed and aged in a dedicated cave carved into the hillside. The farm sells directly at the cave door seasonally; otherwise the cheese appears at Boston and New York fromageries.',
    address: '875 Patch Farm Rd',
    city: 'Putney',
    state: 'VT',
    lat: 42.9820,
    lng: -72.5190,
    image: imageUrl('red-barn-silo'),
    imageKey: 'red-barn-silo',
    hours: 'Cave-door sales in warm months; check website before visiting',
    website: 'https://vermontshepherd.com/',
    highlights: ['Pyrenees-style sheep cheese', 'Hillside aging cave', 'Seasonal release'],
    milkTypes: ['Sheep', 'Cow'],
    styles: ['Aged Hard'],
    tastingAvailable: true,
    visitorPolicy: 'seasonal',
    history:
      'David and Cynthia Major began importing Pyrenees-style cheesemaking knowledge in the early 1990s. Their wheels helped define sheep-milk cheese in the U.S. and inspired a generation of farmstead producers.',
    pairings: ['Black cherry jam', 'Marcona almonds', 'Dry rosé'],
    costRange: '$$$',
  },

  // ----------------------------------------------------------------
  // MAINE
  // ----------------------------------------------------------------
  {
    id: 'pineland-farms-creamery',
    type: 'cheesery',
    verified: true,
    region: 'greater-portland-casco-bay',
    name: 'Pineland Farms Creamery',
    description:
      'A 5,000-acre working farm, education center, and business campus in New Gloucester. The Market & Welcome Center anchors a day-trip of walking trails, visitor dairy barn, and retail shop stocked with the Pineland cheese line — fresh chèvre-style curds, Allagash White–washed rind Cracked Pepper, and aged wheels. Great stop if you’re traveling with family.',
    address: '15 Farm View Dr',
    city: 'New Gloucester',
    state: 'ME',
    lat: 43.9566,
    lng: -70.2700,
    image: imageUrl('dairy-cow-black-white'),
    imageKey: 'dairy-cow-black-white',
    hours: 'Welcome Center daily; Mon–Fri 7:30am–6pm, Sat–Sun 8am–6pm',
    website: 'https://pinelandfarms.org/',
    phone: '(207) 688-4539',
    highlights: ['Working dairy', 'Café and market', 'Walking trails', 'Kid-friendly'],
    milkTypes: ['Cow'],
    styles: ['Aged Hard', 'Cheddar', 'Fresh'],
    tastingAvailable: true,
    visitorPolicy: 'open-to-public',
    history:
      'The campus was purchased by Libra Foundation in 2000 to preserve the former Pineland hospital site as a working agricultural community. The creamery began producing aged cows-milk cheeses in 2005.',
    pairings: ['Allagash White', 'Wild Maine blueberries', 'Crostini'],
    costRange: '$$',
  },
  {
    id: 'silvery-moon-creamery',
    type: 'cheesery',
    verified: true,
    region: 'greater-portland-casco-bay',
    name: 'Silvery Moon Creamery at Smiling Hill Farm',
    description:
      'A small artisan creamery set inside the much larger Smiling Hill Farm — a 500-acre dairy whose farm store, ice cream barn, and family-friendly grounds have been a Portland-area staple since the 1720s. Silvery Moon produces fresh chèvre-style cows-milk cheeses, feta, and aged wheels using milk from the farm’s herd.',
    address: '781 County Rd',
    city: 'Westbrook',
    state: 'ME',
    lat: 43.6742,
    lng: -70.3786,
    image: imageUrl('red-barn-large'),
    imageKey: 'red-barn-large',
    hours: 'Smiling Hill Farm Store open daily; cheese counter staffed most days',
    website: 'https://smilinghillfarm.com/',
    highlights: ['Working dairy since 1720s', 'Ice cream barn', 'Family-friendly farm'],
    milkTypes: ['Cow'],
    styles: ['Fresh', 'Aged Hard'],
    tastingAvailable: true,
    visitorPolicy: 'open-to-public',
    history:
      'Smiling Hill Farm has been owned by the Knight family for nine generations. Silvery Moon was started on-site in 2003 to add value to the farm’s surplus milk.',
    pairings: ['Smiling Hill chocolate milk', 'Sea-salt crackers', 'Roasted beets'],
    costRange: '$',
  },
  {
    id: 'seal-cove-farm',
    type: 'cheesery',
    verified: true,
    region: 'acadia-mount-desert',
    name: 'Seal Cove Farm',
    description:
      'A farmstead goat dairy on the edge of Acadia National Park producing raw-milk chèvre, ricotta, feta, and aged rounds in the French tradition. The farm is a working operation rather than a visitor center, but the farmstand — stocked with fresh cheese, yogurt, and eggs — is a worthwhile detour between Ellsworth and the Park Loop.',
    address: '202 Partridge Cove Rd',
    city: 'Lamoine',
    state: 'ME',
    lat: 44.4689,
    lng: -68.2940,
    image: imageUrl('goat-portrait'),
    imageKey: 'goat-portrait',
    hours: 'Farmstand seasonal; creamery visits typically by appointment',
    website: 'https://mainegoatcheese.com/',
    highlights: ['Near Acadia National Park', 'Raw-milk chèvre', 'French-style methods'],
    milkTypes: ['Goat'],
    styles: ['Fresh', 'Soft Ripened'],
    tastingAvailable: true,
    visitorPolicy: 'by-appointment',
    history:
      'Barbara Brooks started Seal Cove with 20 goats in 1981, pioneering farmstead goat cheese in Maine. The farm relocated to its current Lamoine site in 1996 to expand.',
    pairings: ['Wild Maine blueberry jam', 'Pickled beets', 'Sauvignon Blanc'],
    costRange: '$$',
  },
  {
    id: 'appleton-creamery',
    type: 'cheesery',
    verified: true,
    region: 'midcoast-maine',
    name: 'Appleton Creamery',
    description:
      'A small, highly regarded farmstead creamery in midcoast Maine. Caitlin Hunter makes rich chèvre, ash-rinded pyramids like Appleton Camembert, and sheep-cow blends from her own herd. The creamery is not a drop-in destination, but you’ll see the cheese at Rockland and Belfast farmers markets and in specialty shops statewide.',
    address: '780 Gurneytown Rd',
    city: 'Appleton',
    state: 'ME',
    lat: 44.2800,
    lng: -69.2565,
    image: imageUrl('goat-on-grass'),
    imageKey: 'goat-on-grass',
    hours: 'By appointment; regular at midcoast farmers markets',
    website: 'https://appletoncreamery.com/',
    highlights: ['Farmstead goat + sheep', 'ACS-award winning', 'Midcoast markets'],
    milkTypes: ['Goat', 'Sheep', 'Cow'],
    styles: ['Soft Ripened', 'Fresh'],
    tastingAvailable: true,
    visitorPolicy: 'by-appointment',
    history:
      'Founded by Caitlin Hunter in 1994, Appleton Creamery has earned multiple American Cheese Society awards and helped shape the Maine Cheese Guild, which she co-founded.',
    pairings: ['Honeycomb', 'Rye crackers', 'Belgian witbier'],
    costRange: '$$',
  },
  {
    id: 'crooked-face-creamery',
    type: 'cheesery',
    verified: true,
    region: 'midcoast-maine',
    name: 'Crooked Face Creamery',
    description:
      'A one-woman creamery producing hand-ladled ricotta, smoked ricotta, and small-batch raw-milk wheels. The cheese is sold direct at the Skowhegan Farmers Market and at retail shops around central Maine; visits to the creamery itself are by arrangement.',
    address: 'Skowhegan (exact address by appointment)',
    city: 'Skowhegan',
    state: 'ME',
    lat: 44.7654,
    lng: -69.7192,
    image: imageUrl('cheese-board-charcuterie'),
    imageKey: 'cheese-board-charcuterie',
    hours: 'By appointment; Saturdays at Skowhegan Farmers Market',
    website: 'https://crookedfacecreamery.com/',
    highlights: ['Hand-ladled ricotta', 'Smoked ricotta', 'Central-Maine grain-shed adjacent'],
    milkTypes: ['Cow'],
    styles: ['Fresh', 'Soft Ripened'],
    tastingAvailable: true,
    visitorPolicy: 'by-appointment',
    history:
      'Amy Rowbottom started Crooked Face Creamery in 2013, ladling ricotta in a converted garage. The brand has since grown into a cornerstone of Skowhegan’s Maine Grain Alliance food scene.',
    pairings: ['Maine wild blueberries', 'Skowhegan sourdough', 'Local honey'],
    costRange: '$$',
  },
  {
    id: 'balfour-farm',
    type: 'cheesery',
    verified: true,
    region: 'midcoast-maine',
    name: 'Balfour Farm Dairy',
    description:
      'A small certified-organic farmstead dairy producing raw-milk cheddar, halloumi, feta, and fresh cheeses from their Normande herd. The on-farm store in Pittsfield is open limited hours each week and stocks cheese, yogurt, butter, and pasture-raised meats.',
    address: '461 Webb Rd',
    city: 'Pittsfield',
    state: 'ME',
    lat: 44.7670,
    lng: -69.3750,
    image: imageUrl('red-barn-silo'),
    imageKey: 'red-barn-silo',
    hours: 'Farm store open limited weekly hours — check site before visiting',
    website: 'https://balfourfarmdairy.com/',
    highlights: ['Certified organic', 'Normande herd', 'Halloumi + cheddar'],
    milkTypes: ['Cow'],
    styles: ['Cheddar', 'Fresh', 'Aged Hard'],
    tastingAvailable: true,
    visitorPolicy: 'open-to-public',
    history:
      'Heather and Doug Donahue moved to central Maine in 2009 to run a diversified organic dairy. Balfour is one of very few certified-organic raw-milk cheesemakers in the state.',
    pairings: ['Maine wild blueberry jam', 'Grilled peaches', 'Farmhouse saison'],
    costRange: '$$',
  },

  // ==================================================================
  // NEW HAMPSHIRE
  //
  // Verified 2026-04-19 against each producer's official website and
  // publicly-listed contact directories. Hours drift seasonally; plan
  // to re-check each spring and bump `lastVerified`.
  // ==================================================================
  {
    id: 'sandwich-creamery',
    type: 'cheesery',
    verified: true,
    lastVerified: '2026-04-19',
    region: 'white-mountains-nh',
    name: 'Sandwich Creamery',
    description:
      'A small farmstead creamery tucked into a valley at the northern edge of the Lakes Region, established in 1995 and still producing goat and cow cheeses from its own herd \u2014 including an aged cheddar that NH Farms Network credits as the only one made in the state \u2014 alongside smoked cheddar, Caerphilly, and farm-made ice cream. Runs a self-serve farm store on the honor system: weigh your own cheese, leave payment in the box.',
    address: '2 Maple Ridge Rd',
    city: 'North Sandwich',
    state: 'NH',
    lat: 43.8620,
    lng: -71.4240,
    image: imageUrl('goat-portrait'),
    imageKey: 'goat-portrait',
    hours: 'Self-serve farm store open daily 9am\u20139pm (honor system)',
    website: 'https://sandwichcreamery.com/',
    highlights: ['Self-serve honor-system store', 'NH\u2019s only farmstead aged cheddar', 'Open daily 9am\u20139pm'],
    milkTypes: ['Goat', 'Cow', 'Mixed'],
    styles: ['Aged Hard', 'Fresh', 'Soft Ripened'],
    tastingAvailable: true,
    visitorPolicy: 'open-to-public',
    history:
      'Established in 1995 by Tom Merriman and now operated by Will Johnston. One of the longest-running farmstead creameries in New Hampshire, and the only producer in the state making farmstead aged cheddar according to the NH Farms Network profile.',
    pairings: ['White Mountain apples', 'Local honey', 'Hard cider'],
    costRange: '$$',
  },
  {
    id: 'harmans-cheese-country-store',
    type: 'cheesery',
    verified: true,
    lastVerified: '2026-04-19',
    region: 'white-mountains-nh',
    name: 'Harman\u2019s Cheese & Country Store',
    description:
      'A Sugar Hill institution since the 1950s and the rare country store whose business truly is cheese \u2014 not a side line. The case ages Vermont-style clothbound cheddar on premises (marketed as \u201cthe world\u2019s greatest cheddar\u201d) and carries a tightly-curated roster of other New Hampshire makers: Huckin\u2019s Farm cheeses and yogurt from New Hampton, Fox Country Smoke House sausages from Canterbury, fresh breads from Polly\u2019s Pancake Parlor up the road. The best single-stop \u201cdeparture hub\u201d for a White Mountains cheese weekend.',
    address: '1400 NH-117',
    city: 'Sugar Hill',
    state: 'NH',
    lat: 44.1950,
    lng: -71.8070,
    image: imageUrl('cheese-board-charcuterie'),
    imageKey: 'cheese-board-charcuterie',
    hours: 'Mon\u2013Sat 9am\u20134:30pm, Sun 9am\u20132:30pm (check site for seasonal variation)',
    website: 'https://harmanscheese.com/',
    highlights: ['Historic NH retail icon (since the 1950s)', 'On-site aged cheddar', 'Curates other NH makers'],
    milkTypes: ['Cow'],
    styles: ['Cheddar', 'Aged Hard'],
    tastingAvailable: true,
    visitorPolicy: 'open-to-public',
    history:
      'A Sugar Hill retail institution dating to the 1950s, long associated with aged cheddar and a curated lineup of New England pantry goods. Seasonal \u201cVendors on the Lawn\u201d events run around Memorial Day weekend and early June.',
    pairings: ['Cortland apples', 'NH maple syrup', 'Stone-ground crackers'],
    costRange: '$$',
  },
  {
    id: 'robie-farm',
    type: 'cheesery',
    verified: true,
    lastVerified: '2026-04-19',
    region: 'white-mountains-nh',
    name: 'Robie Farm & Store',
    description:
      'A seventh-generation family farm on the Connecticut River, in the same family since 1870. About twenty cows in the milking parlor supply the farm\u2019s raw milk, three on-site cheeses \u2014 Piermont (a tangy semi-soft named for the village), hickory-smoked, and Toma in the Alpine table-cheese tradition \u2014 plus pasture-raised beef, pork, chicken, and farm eggs. The staffed farm store is one of the most visitor-friendly stops on the trail.',
    address: '25 NH-10',
    city: 'Piermont',
    state: 'NH',
    lat: 43.9930,
    lng: -72.0740,
    image: imageUrl('red-barn-silo'),
    imageKey: 'red-barn-silo',
    hours: 'Mon\u2013Fri 9am\u20136pm, Sat 9am\u20135pm, Sun 12:30pm\u20133pm (closed Easter, Thanksgiving, Christmas)',
    website: 'https://robiefarmnh.com/',
    highlights: ['Seventh-generation (since 1870)', 'Raw-milk cheese + pasture meats', 'Staffed farm store six days a week'],
    milkTypes: ['Cow'],
    styles: ['Cheddar', 'Aged Hard', 'Fresh'],
    tastingAvailable: false,
    visitorPolicy: 'open-to-public',
    history:
      'The Robie family has worked the land in Piermont since 1870. The current operation \u2014 now in its seventh generation \u2014 milks approximately twenty cows and makes cheese on-site, with a staffed farm store that has become a fixture of the Upper Valley.',
    pairings: ['Apple butter', 'Sourdough', 'Local cider'],
    costRange: '$$',
  },
  {
    id: 'brookford-farm',
    type: 'cheesery',
    verified: true,
    lastVerified: '2026-04-19',
    region: 'merrimack-valley-nh',
    name: 'Brookford Farm',
    description:
      'A 600-acre biodynamic and organic farm in Canterbury whose farm store is a rare one-stop source for raw cow\u2019s milk, cultured butter, yogurt, fresh and aged cheeses from the farm\u2019s own Jersey herd, pastured meats, eggs, certified-organic vegetables, and Brookford\u2019s lacto-fermented sauerkraut and kimchi. CSA membership available. Adjacent to historic Canterbury Shaker Village.',
    address: '250 West Rd',
    city: 'Canterbury',
    state: 'NH',
    lat: 43.3585,
    lng: -71.5942,
    image: imageUrl('dairy-cow-black-white'),
    imageKey: 'dairy-cow-black-white',
    hours: 'Farm store open limited weekly hours \u2014 check site before visiting',
    website: 'https://brookfordfarm.com/',
    highlights: ['Biodynamic + certified organic', 'Raw milk, butter, yogurt, cheese', 'Near Canterbury Shaker Village'],
    milkTypes: ['Cow'],
    styles: ['Fresh', 'Aged Hard'],
    tastingAvailable: true,
    visitorPolicy: 'open-to-public',
    history:
      'Founded by Catarina and Luke Mahoney, who met in 1999 on a farm near St. Petersburg, Russia. After operating in Rollingsford, NH, the farm relocated to its current 600-acre Canterbury site with funding support from Gary and Meg Hirshberg (co-founders of Stonyfield). The farm now runs 35+ acres of certified-organic vegetables alongside the dairy, plus cattle, hogs, lambs, and chickens.',
    pairings: ['Brookford sauerkraut', 'Rye bread', 'Dark lager'],
    costRange: '$$',
  },
  {
    id: 'contoocook-creamery',
    type: 'cheesery',
    verified: true,
    lastVerified: '2026-04-19',
    region: 'merrimack-valley-nh',
    name: 'Contoocook Creamery at Bohanan Farm',
    description:
      'A working family dairy and creamery along the Contoocook River, in operation since the early 1900s. The on-farm store is small, self-service, and \u2014 remarkably \u2014 open during daylight hours seven days a week, 365 days a year. Stock includes non-homogenized milk in glass and plastic bottles, eggs, individually-wrapped frozen beef, seasonal vegetables, and the creamery\u2019s own cheese. Visitors are welcome to wander to the milking parlor at milking time.',
    address: '945 Penacook Rd',
    city: 'Contoocook',
    state: 'NH',
    lat: 43.2220,
    lng: -71.7150,
    image: imageUrl('dairy-cow-black-white'),
    imageKey: 'dairy-cow-black-white',
    hours: 'Self-service farm store open daylight hours, 7 days a week, 365 days a year',
    website: 'https://contoocookcreamery.com/',
    highlights: ['Open 365 days a year', 'Self-service farm store', 'Glass-bottled milk', 'Visitors can watch milking'],
    milkTypes: ['Cow'],
    styles: ['Fresh'],
    tastingAvailable: true,
    visitorPolicy: 'open-to-public',
    history:
      'Bohanan Farm has been in the family since the early 1900s, when founder Lester Bohanan began farming alongside his railroad work. The creamery operation launched in 2011 under great-granddaughter Heather Bohanan to add value to the farm\u2019s milk and combat low wholesale dairy prices. In 2009, Five Rivers Conservation Trust helped secure a 413-acre conservation easement on the land.',
    pairings: ['NH maple syrup', 'Concord cider donuts', 'Wild blueberries'],
    costRange: '$',
  },
];
